import { type NetworkService, serviceLabel } from "../../lib/networkServices";
import { type Lane, laneLabel } from "../../lib/sources";
import {
  EmailIcon,
  FirewallIcon,
  GlobeIcon,
  NetworkIcon,
  PhoneIcon,
  RdpIcon,
  RemoteIcon,
  RmmIcon,
  ServerIcon,
  TerminalIcon,
} from "./Icons";

type Icon = () => React.JSX.Element;

const ICONS: Record<Lane, Icon> = {
  phone: PhoneIcon,
  remote: RemoteIcon,
  rmm: RmmIcon,
  email: EmailIcon,
  network: NetworkIcon,
};

// Each network service's (ADR 0165). Other is the network's own.
const SERVICE_ICONS: Record<NetworkService, Icon> = {
  ssh: TerminalIcon,
  rdp: RdpIcon,
  firewall_management: FirewallIcon,
  server_management: ServerIcon,
  web_https: GlobeIcon,
  other: NetworkIcon,
};

/**
 * A 26px icon tile. The icon is paired with its name for screen readers and
 * on hover (section 11.3), unless `decorative`: beside the name shown in
 * words, it's only a picture (Idea #525).
 */
function Tile({
  Icon,
  label,
  onBrand,
  decorative,
}: {
  Icon: Icon;
  label: string;
  onBrand: boolean;
  decorative: boolean;
}) {
  return (
    <span
      title={decorative ? undefined : label}
      aria-hidden={decorative || undefined}
      className={
        "flex size-[26px] shrink-0 items-center justify-center rounded-icon " +
        (onBrand ? "bg-white/18 text-white" : "bg-brand-tint text-brand-ink-on-tint")
      }
    >
      <Icon />
      {decorative ? null : <span className="sr-only">{label}</span>}
    </span>
  );
}

/** A source's icon tile, named for its lane. */
export function SourceTile({
  lane,
  onBrand = false,
  decorative = false,
}: {
  lane: Lane;
  onBrand?: boolean;
  decorative?: boolean;
}) {
  return <Tile Icon={ICONS[lane]} label={laneLabel(lane)} onBrand={onBrand} decorative={decorative} />;
}

/** A network activity's service's icon tile, named for its service (ADR 0165). */
export function ServiceTile({
  service,
  onBrand = false,
  decorative = false,
}: {
  service: NetworkService;
  onBrand?: boolean;
  decorative?: boolean;
}) {
  return (
    <Tile Icon={SERVICE_ICONS[service]} label={serviceLabel(service)} onBrand={onBrand} decorative={decorative} />
  );
}
